/**
 * Utility to suppress specific console warnings from third-party libraries
 */

let originalWarn: typeof console.warn | null = null
let originalError: typeof console.error | null = null

interface SuppressOptions {
  suppressRechartsWarnings?: boolean
  suppressFirebaseWarnings?: boolean
  customFilters?: ((message: string) => boolean)[]
}

/**
 * Enable console warning suppression with configurable filters
 * @param options - Configuration for which warnings to suppress
 */
export const enableConsoleSupression = (options: SuppressOptions = {}) => {
  const {
    suppressRechartsWarnings = true,
    suppressFirebaseWarnings = false,
    customFilters = []
  } = options

  // Store original console methods if not already stored
  if (!originalWarn) {
    originalWarn = console.warn
  }
  if (!originalError) {
    originalError = console.error
  }

  // Override console.warn
  console.warn = (...args: any[]) => {
    const message = args[0]?.toString() || ''

    // Suppress Recharts defaultProps warnings
    if (suppressRechartsWarnings && message.includes('defaultProps will be removed')) {
      return
    }

    // Suppress Firebase 400 errors (connection termination)
    if (suppressFirebaseWarnings && message.includes('400 (Bad Request)')) {
      return
    }

    // Apply custom filters
    for (const filter of customFilters) {
      if (filter(message)) {
        return
      }
    }

    // Call original warn for non-suppressed messages
    originalWarn?.apply(console, args)
  }

  // Optionally suppress specific console.error messages
  if (suppressFirebaseWarnings) {
    console.error = (...args: any[]) => {
      const message = args[0]?.toString() || ''

      // Suppress Firebase Firestore connection termination errors
      if (message.includes('Firestore/Write/channel') && message.includes('400')) {
        return
      }

      originalError?.apply(console, args)
    }
  }
}

/**
 * Disable console warning suppression and restore original behavior
 */
export const disableConsoleSupression = () => {
  if (originalWarn) {
    console.warn = originalWarn
    originalWarn = null
  }
  if (originalError) {
    console.error = originalError
    originalError = null
  }
}

/**
 * Add a custom filter for console warnings
 * @param filter - Function that returns true to suppress the message
 */
export const addCustomWarningFilter = (filter: (message: string) => boolean) => {
  const currentWarn = console.warn
  console.warn = (...args: any[]) => {
    const message = args[0]?.toString() || ''
    if (filter(message)) {
      return
    }
    currentWarn.apply(console, args)
  }
}